import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, CheckCircle2, XCircle, Mail, MapPin, Clock } from "lucide-react";

import {
  RevealSection,
  SplitText,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// Same theme tokens as Work.jsx / Careers.jsx / Solutions.jsx.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// NEW hero background — "signal transmission" motif, distinct from the
// aurora field (Work), rising-particle growth field (Careers), and
// blueprint tile field (Solutions). Concentric rings ping outward from
// a fixed origin like a message being sent, small packets travel in
// straight diagonal lines across the hero, and a soft glow breathes
// behind the origin point.
// ---------------------------------------------------------------------
const packets = [
  { top: "18%", delay: 0, duration: 7, gold: false },
  { top: "34%", delay: 1.4, duration: 9, gold: true },
  { top: "52%", delay: 2.8, duration: 6.5, gold: false },
  { top: "68%", delay: 0.8, duration: 8.5, gold: true },
  { top: "80%", delay: 3.6, duration: 7.5, gold: false },
];

function SignalField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <DotGrid light />
      <span className="signal-glow" />
      <span className="signal-ring" />
      <span className="signal-ring signal-ring-2" />
      <span className="signal-ring signal-ring-3" />
      <span className="signal-origin" />

      {packets.map((p, i) => (
        <span
          key={i}
          className="signal-packet"
          style={{
            top: p.top,
            backgroundColor: p.gold ? theme.gold : theme.accent,
            boxShadow: `0 0 10px 2px ${p.gold ? theme.gold : theme.accent}77`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function Contact() {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [form, setForm] = useState({ user_name: "", user_email: "", message: "" });
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");

    try {
      const res = await fetch("http://127.0.0.1:5000/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Request failed");

      await res.json();
      setStatus("success");
      setForm({ user_name: "", user_email: "", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch (error) {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    show: { opacity: 1, y: 0 },
  };

  const inputClasses =
    "w-full px-4 py-3.5 rounded-xl text-white placeholder:text-white/35 focus:outline-none transition-colors";

  return (
    <div className="w-full" style={{ backgroundColor: theme.heroBg }}>
      <style>{`
        .signal-glow {
          position: absolute; top: 50%; left: 50%; width: 30rem; height: 30rem;
          margin: -15rem 0 0 -15rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}35, transparent 68%);
          filter: blur(60px);
          animation: signalGlowBreathe 6s ease-in-out infinite;
        }
        @keyframes signalGlowBreathe {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        .signal-origin {
          position: absolute; top: 50%; left: 50%; width: 8px; height: 8px;
          margin: -4px 0 0 -4px; border-radius: 9999px;
          background: ${theme.accent};
          box-shadow: 0 0 14px 4px ${theme.accent}aa;
        }

        .signal-ring {
          position: absolute; top: 50%; left: 50%; width: 4px; height: 4px;
          margin: -2px 0 0 -2px; border-radius: 9999px;
          border: 1px solid ${theme.accent};
          animation: signalPing 4.5s ease-out infinite;
        }
        .signal-ring-2 { border-color: ${theme.gold}; animation-delay: 1.5s; }
        .signal-ring-3 { border-color: rgba(255,255,255,0.5); animation-delay: 3s; }
        @keyframes signalPing {
          0% { width: 4px; height: 4px; margin: -2px 0 0 -2px; opacity: 0.8; }
          100% { width: 32rem; height: 32rem; margin: -16rem 0 0 -16rem; opacity: 0; }
        }

        .signal-packet {
          position: absolute; left: -4%; width: 6px; height: 6px; border-radius: 9999px;
          animation-name: signalPacketTravel;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        @keyframes signalPacketTravel {
          0% { transform: translate(0, 0); opacity: 0; }
          8% { opacity: 1; }
          92% { opacity: 1; }
          100% { transform: translate(112vw, -3rem); opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .signal-glow, .signal-ring, .signal-ring-2, .signal-ring-3, .signal-packet {
            animation: none !important;
          }
        }

        .contact-panel {
          background-color: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
        }
        .contact-input {
          background-color: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.12);
        }
        .contact-input:focus {
          border-color: ${theme.accent};
          box-shadow: 0 0 0 3px ${theme.accent}33;
        }
      `}</style>

      {/* TOAST */}
      <AnimatePresence>
        {(status === "success" || status === "error") && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 20, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="fixed top-0 left-1/2 -translate-x-1/2 font-semibold px-6 py-4 rounded-b-xl shadow-lg z-50 flex items-center gap-2"
            style={{
              backgroundColor: status === "success" ? theme.accent : "#B91C1C",
              color: "#FFFFFF",
            }}
          >
            {status === "success" ? (
              <>
                <CheckCircle2 size={20} />
                Your message has been sent!
              </>
            ) : (
              <>
                <XCircle size={20} />
                Something went wrong — please try again.
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= HERO (dark, signal field bg) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-24 md:pt-48 md:pb-28" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <SignalField />
        </motion.div>

        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow center light>Get In Touch</Eyebrow>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
              <SplitText text="Contact" className="text-white" />{" "}
              <span style={{ color: theme.accent }}>
                <SplitText text="UniBcomp" delay={0.2} />
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
              className="text-white/60 text-lg"
            >
              Have a question or want to collaborate?
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ================= FORM + INFO (dark) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div className="relative z-10 max-w-6xl mx-auto px-6 grid md:grid-cols-5 gap-10">
          {/* Form */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="contact-panel md:col-span-3 rounded-3xl p-8 md:p-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">
              Send Us a <span style={{ color: theme.accent }}>Message</span>
            </h2>
            <p className="text-white/50 text-sm mb-8">
              We usually reply within one business day.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="user_name" className="sr-only">
                  Your Name
                </label>
                <input
                  id="user_name"
                  type="text"
                  name="user_name"
                  value={form.user_name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  className={`${inputClasses} contact-input`}
                  required
                />
              </div>

              <div>
                <label htmlFor="user_email" className="sr-only">
                  Your Email
                </label>
                <input
                  id="user_email"
                  type="email"
                  name="user_email"
                  value={form.user_email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  className={`${inputClasses} contact-input`}
                  required
                />
              </div>

              <div>
                <label htmlFor="message" className="sr-only">
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Your Message"
                  rows="6"
                  className={`${inputClasses} contact-input resize-none`}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={status === "sending"}
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-4 rounded-md hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact info */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="md:col-span-2 flex flex-col gap-6"
          >
            <div className="contact-panel rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                >
                  <Mail size={18} />
                </div>
                <h3 className="text-white font-semibold">Email</h3>
              </div>
              <p className="text-white/50 text-sm pl-[52px]">
                Fill out the form and we'll get back to you shortly.
              </p>
            </div>

            <div className="contact-panel rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                >
                  <MapPin size={18} />
                </div>
                <h3 className="text-white font-semibold">Location</h3>
              </div>
              <p className="text-white/50 text-sm pl-[52px]">Gurugram, India</p>
            </div>

            <div className="contact-panel rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                >
                  <Clock size={18} />
                </div>
                <h3 className="text-white font-semibold">Response Time</h3>
              </div>
              <p className="text-white/50 text-sm pl-[52px]">
                Typically within one business day.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}